import React, { useEffect, useRef, useState, useCallback } from 'react';
import { View, StyleSheet, Platform, Dimensions } from 'react-native';
import { useHeaderHeight } from '@react-navigation/elements';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from '../store';
import { userActions } from '../store/slices/user-slice';
import ChargeAgainCard from './ChargeAgainCard';

interface Props {
  children: React.ReactNode;
  currentRouteName: string | undefined;
  offset?: number;
  showChargeAgain?: boolean;
  onDismiss?: () => void;
}

const ChargeAgainWrapper: React.FC<Props> = ({
  children,
  currentRouteName,
  offset = 48,
  showChargeAgain = true,
  onDismiss,
}) => {
  const [shouldShowCard, setShouldShowCard] = useState(showChargeAgain);
  const primaryUse = useSelector(
    (state: RootState) => state.user.session?.primary_use
  );
  const dispatch: AppDispatch = useDispatch();
  const activeCharges = useSelector(
    (state: RootState) => state.user.activeCharges
  );

  // Define screens where the host mode banner should be hidden
  const hideBannerScreens = [
    'OnboardingStatus',
    'SetupBilling', 
    'ChargerValidation'
  ];

  // Check if current screen should hide the banner
  const routeName = currentRouteName === 'undefined' ? undefined : currentRouteName;
  const shouldHideBanner = !!routeName && hideBannerScreens.includes(routeName);

  // Update shouldShowCard when showChargeAgain prop changes
  useEffect(() => {
    setShouldShowCard(showChargeAgain);
    // console.log(
    //   'ChargeAgainWrapper: showChargeAgain changed to:',
    //   showChargeAgain
    // );
  }, [showChargeAgain]);

  let headerHeight: number;
  try {
    headerHeight = useHeaderHeight();
  } catch (error) {
    headerHeight = 10;
  }

  const handleDismiss = useCallback(() => {
    setShouldShowCard(false);
    // Update Redux state to persist the dismissal
    dispatch(userActions.setHasShownChargeAgain(true));
    onDismiss?.();
  }, [onDismiss, dispatch]);

  // Calculate dynamic positioning
  const getCardTopPosition = () => {
    const { height: screenHeight } = Dimensions.get('window');

    // Base position below dynamic island/status bar
    let baseTop = headerHeight + 5; // 20px padding from header

    // On iOS, account for dynamic island
    if (Platform.OS === 'ios') {
      // Add extra space for dynamic island if present
      baseTop += 0;
    }

    // Account for HostModeWrapper banner if visible and not hidden
    if (primaryUse === 'host' && !shouldHideBanner) {
      const hostBannerHeight = Platform.OS === 'ios' ? 51 : 28; // Height from HostModeWrapper
      baseTop += hostBannerHeight + 10; // Add banner height + 10px padding
    }

    // Add offset if provided
    baseTop += offset;
    return baseTop;
  };

  // Calculate the threshold for dismissal (when card gets too high)
  const getDismissalThreshold = () => {
    return 0;
  };

  // Check if card should be dismissed based on its position
  const shouldDismissCard = () => {
    const cardTopPosition = getCardTopPosition();
    const dismissalThreshold = getDismissalThreshold();

    // If the card's top position is above the safe zone, dismiss it
    return cardTopPosition < dismissalThreshold;
  };

  // Effect to check position and dismiss if needed
  useEffect(() => {
    if (shouldShowCard && shouldDismissCard()) {
      // console.log('ChargeAgainWrapper: Card position too high, dismissing');
      handleDismiss();
    }
  }, [shouldShowCard, offset, primaryUse, headerHeight, handleDismiss]);

  // Debug logging
  // console.log(
  //   `ChargeAgainWrapper: cardTop=${getCardTopPosition()}, dismissalThreshold=${getDismissalThreshold()}, shouldDismiss=${shouldDismissCard()}, shouldShowCard=${shouldShowCard}, showChargeAgain=${showChargeAgain}`
  // );

  return (
    <View style={styles.wrapper}>
      {children}

      {/* Conditionally render the ChargeAgainCard */}
      <View
        style={[
          styles.chargeCardContainer,
          {
            position: 'absolute',
            top: getCardTopPosition(),
            left: 0,
            right: 0,
          },
        ]}
        pointerEvents="box-none"
      >
        {shouldShowCard && (!activeCharges || activeCharges.length === 0) && (
          <View pointerEvents="auto">
            <ChargeAgainCard
              onDismiss={handleDismiss}
              baseTop={getCardTopPosition()}
            />
          </View>
        )}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  wrapper: {
    flex: 1,
    position: 'relative',
    width: '100%',
  },
  chargeCardContainer: {
    padding: 10,
    zIndex: 1000,
    alignItems: 'center',
  },
});

export default ChargeAgainWrapper;
